import React from "react";
import { Building2, CheckCircle2, ArrowRight } from "lucide-react";

const BLACK_CAR =
  "https://static.prod-images.emergentagent.com/jobs/34467e70-7230-427a-9b62-9be3c11e38b7/images/9a51ffdc057d4e3bde36353171a81de418c8976f11c78c58aaada91ad9c21918.jpeg";
const LOGO = "/assets/logo.png";

const VB_W = 1000, VB_H = 520;
const pctX = (x) => `${(x / VB_W) * 100}%`;
const pctY = (y) => `${(y / VB_H) * 100}%`;

// Instituciones con coordenadas (centro) en el espacio del viewBox
const BADGES = [
  { name: "BBVA", bg: "#004481", color: "#fff", cx: 270, cy: 78, ax: 418, ay: 200 },
  { name: "BANORTE", bg: "#111", color: "#fff", cx: 192, cy: 162, ax: 410, ay: 222, dot: "#EB0029" },
  { name: "Santander", bg: "#EC0000", color: "#fff", cx: 190, cy: 258, ax: 406, ay: 252 },
  { name: "BanRegio", bg: "#fff", color: "#111", cx: 214, cy: 352, ax: 412, ay: 288, accent: "#E4002B" },
  { name: "hey,", sub: "banco", bg: "#C4F000", color: "#111", cx: 204, cy: 444, ax: 420, ay: 315 },
  { name: "ARRENDADORAS Y FINANCIERAS", bg: "#1a1a1a", color: "#fff", cx: 328, cy: 452, ax: 432, ay: 330, icon: true, small: true },
];

const NODE = { cx: 486, cy: 250, r: 82 };

function connector({ cx, cy, ax, ay }) {
  const c1x = cx + (ax - cx) * 0.55, c1y = cy;
  const c2x = ax - (ax - cx) * 0.25, c2y = ay;
  return `M ${cx} ${cy} C ${c1x} ${c1y} ${c2x} ${c2y} ${ax} ${ay}`;
}

function Badge({ b, size }) {
  const d = size; // px handled via % width
  return (
    <div
      className="absolute rounded-full aspect-square flex flex-col items-center justify-center text-center shadow-lg"
      style={{
        left: pctX(b.cx), top: pctY(b.cy), width: `${(d / VB_W) * 100}%`,
        transform: "translate(-50%,-50%)", background: b.bg, color: b.color,
        boxShadow: "0 0 22px -4px rgba(255,133,0,0.55)", border: b.bg === "#fff" ? "none" : "1px solid rgba(255,255,255,0.08)",
      }}
    >
      {b.icon ? (
        <>
          <Building2 size={22} />
          <span className="font-bold leading-tight px-1" style={{ fontSize: "0.5rem", marginTop: 2 }}>{b.name}</span>
        </>
      ) : b.sub ? (
        <span className="font-bold leading-none" style={{ fontSize: "0.95rem" }}>{b.name}<br /><span style={{ fontSize: "0.55rem", fontWeight: 500 }}>{b.sub}</span></span>
      ) : (
        <span className="font-bold leading-tight px-1" style={{ fontSize: b.small ? "0.5rem" : "0.8rem" }}>
          {b.accent ? <>Ban<span style={{ color: b.accent }}>Regio</span></> : b.name}
        </span>
      )}
    </div>
  );
}

export default function BusinessEngineDiagram() {
  return (
    <div className="af-card p-6 md:p-10 mt-12 relative overflow-hidden">
      {/* ---------- Desktop / large: canvas con conectores ---------- */}
      <div className="hidden lg:block relative w-full" style={{ paddingBottom: `${(VB_H / VB_W) * 100}%` }}>
        <div className="absolute inset-0">
          <svg viewBox={`0 0 ${VB_W} ${VB_H}`} className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet" style={{ overflow: "visible" }}>
            <defs>
              <filter id="beGlow" x="-40%" y="-40%" width="180%" height="180%">
                <feGaussianBlur stdDeviation="3.2" result="b" />
                <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <radialGradient id="nodeGrad" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#1a1a1a" />
                <stop offset="100%" stopColor="#0d0d0d" />
              </radialGradient>
              <marker id="arrowHead" markerWidth="12" markerHeight="12" refX="6" refY="6" orient="auto">
                <path d="M1,1 L11,6 L1,11 Z" fill="#FF8500" />
              </marker>
            </defs>

            {/* connectors */}
            {BADGES.map((b, i) => (
              <path key={i} d={connector(b)} fill="none" stroke="#FF8500" strokeWidth="2.6"
                strokeLinecap="round" className="flow-line" filter="url(#beGlow)" opacity="0.9" />
            ))}

            {/* node rings */}
            <circle cx={NODE.cx} cy={NODE.cy} r={NODE.r + 34} fill="none" stroke="rgba(255,133,0,0.25)" strokeWidth="1.5" />
            <circle cx={NODE.cx} cy={NODE.cy} r={NODE.r + 16} fill="none" stroke="rgba(255,133,0,0.45)" strokeWidth="2" filter="url(#beGlow)" />
            <circle cx={NODE.cx} cy={NODE.cy} r={NODE.r} fill="url(#nodeGrad)" stroke="#E85D04" strokeWidth="2.5" filter="url(#beGlow)" />

            {/* arrow to car */}
            <line x1={NODE.cx + NODE.r + 4} y1={NODE.cy} x2={NODE.cx + NODE.r + 92} y2={NODE.cy}
              stroke="#FF8500" strokeWidth="5" strokeLinecap="round" markerEnd="url(#arrowHead)" filter="url(#beGlow)" />
          </svg>

          {/* AF logo over node */}
          <img src={LOGO} alt="AF" className="absolute" style={{ left: pctX(NODE.cx), top: pctY(NODE.cy), transform: "translate(-50%,-50%)", width: "11%" }} />

          {/* badges */}
          {BADGES.map((b, i) => <Badge key={i} b={b} size={b.small ? 108 : 96} />)}

          {/* car + ground glow */}
          <div className="absolute be-ground" style={{ left: pctX(805), top: pctY(360), transform: "translate(-50%,-50%)", width: "34%", height: "16%" }} />
          <img src={BLACK_CAR} alt="Vehículo recomendado" className="absolute rounded-lg"
            style={{ left: pctX(808), top: pctY(258), transform: "translate(-50%,-50%)", width: "35%", mixBlendMode: "lighten" }} />
          <CheckCircle2 className="absolute text-af-orange-core" size={40}
            style={{ left: pctX(700), top: pctY(212), transform: "translate(-50%,-50%)", background: "#0d0d0d", borderRadius: "50%" }} />

          {/* labels */}
          <div className="absolute text-left" style={{ left: pctX(20), top: pctY(250), transform: "translateY(-50%)", width: "16%" }}>
            <div className="text-white font-bold leading-tight" style={{ fontSize: "0.95rem" }}>MÚLTIPLES INSTITUCIONES</div>
            <div className="text-white/60 mt-2" style={{ fontSize: "0.72rem", lineHeight: 1.4 }}>Conectamos con las principales instituciones financieras para ti.</div>
          </div>

          <div className="absolute text-center" style={{ left: pctX(NODE.cx), top: pctY(64), transform: "translate(-50%,-50%)" }}>
            <div className="text-af-orange-core font-bold leading-tight" style={{ fontSize: "1rem" }}>AUTOFINANZA<br />BUSINESS ENGINE</div>
          </div>
          <div className="absolute text-center text-white/70" style={{ left: pctX(NODE.cx), top: pctY(392), transform: "translate(-50%,-50%)", width: "26%", fontSize: "0.72rem", lineHeight: 1.4 }}>
            Nuestra tecnología analiza tu perfil y compara en segundos miles de combinaciones.
          </div>

          <div className="absolute text-left" style={{ left: pctX(720), top: pctY(96), width: "26%" }}>
            <div className="text-white font-bold leading-tight" style={{ fontSize: "1rem" }}>LA MEJOR OPCIÓN<br />PARA TU PERFIL</div>
            <div className="text-white/60 mt-2" style={{ fontSize: "0.72rem", lineHeight: 1.4 }}>Te presentamos la alternativa que mejor se adapta a ti.</div>
          </div>
        </div>
      </div>

      {/* ---------- Mobile: versión apilada ---------- */}
      <div className="lg:hidden">
        <div className="text-center">
          <div className="text-white font-bold text-lg">MÚLTIPLES INSTITUCIONES</div>
          <p className="text-white/60 text-sm mt-1 mb-5">Conectamos con las principales instituciones financieras para ti.</p>
          <div className="flex flex-wrap justify-center gap-3">
            {BADGES.map((b, i) => (
              <div key={i} className="rounded-full flex flex-col items-center justify-center text-center shrink-0"
                style={{ width: 76, height: 76, background: b.bg, color: b.color, boxShadow: "0 0 18px -4px rgba(255,133,0,0.5)" }}>
                {b.icon ? <><Building2 size={20} /><span className="font-bold leading-tight px-1" style={{ fontSize: "0.42rem" }}>{b.name}</span></>
                  : b.sub ? <span className="font-bold leading-none text-sm">{b.name}<br /><span style={{ fontSize: "0.5rem" }}>{b.sub}</span></span>
                  : <span className="font-bold text-xs px-1">{b.accent ? <>Ban<span style={{ color: b.accent }}>Regio</span></> : b.name}</span>}
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col items-center my-8">
          <div className="text-af-orange-core font-bold text-center mb-3">AUTOFINANZA BUSINESS ENGINE</div>
          <div className="relative flex items-center justify-center" style={{ width: 150, height: 150 }}>
            <div className="absolute inset-0 rounded-full animate-pulse-ring" style={{ border: "2px solid rgba(232,93,4,0.5)", boxShadow: "0 0 50px -6px rgba(255,133,0,0.6)" }} />
            <img src={LOGO} alt="AF" style={{ width: 82 }} />
          </div>
          <p className="text-white/70 text-sm text-center mt-4 max-w-xs">Nuestra tecnología analiza tu perfil y compara en segundos miles de combinaciones.</p>
          <ArrowRight className="text-af-orange-core rotate-90 my-4" size={28} />
        </div>
        <div className="text-center">
          <div className="text-white font-bold text-lg">LA MEJOR OPCIÓN PARA TU PERFIL</div>
          <p className="text-white/60 text-sm mt-1 mb-4">Te presentamos la alternativa que mejor se adapta a ti.</p>
          <div className="relative">
            <div className="absolute left-1/2 -translate-x-1/2 be-ground" style={{ bottom: 8, width: "70%", height: 40 }} />
            <img src={BLACK_CAR} alt="Vehículo recomendado" className="w-full rounded-xl relative" style={{ mixBlendMode: "lighten" }} />
            <CheckCircle2 size={40} className="text-af-orange-core absolute top-2 left-4" style={{ background: "#0d0d0d", borderRadius: "50%" }} />
          </div>
        </div>
      </div>
    </div>
  );
}
